import { Header } from '@services/MarppleShop/renderApp/components/cells/Header/Global/Header.ts';
import { applySubHeaderHeadroom } from '@services/MarppleShop/renderApp/components/cells/Header/libs/helpers/headroom.ts';
import { html, Page, View } from 'rune-ts';
import sanitizer from 'sanitizer';
import { BaseLayoutOptions } from '../../../features/Core/service';
import { ClientPopupProps, Popup } from '../../../shared/components/cells/Popup/Popup';
import { Locals } from '../../../shared/type/locals';
import { getSharedData } from '../../../shared/util/rune';
import { footer_links } from '../../components/cells/Footer/constant';
import { LayoutView } from '../../components/cells/Layout/LayoutView';
import { SubHeaderMobile } from '../../components/cells/Header/Global/views/SubHeaderMobile.ts';
import { SubHeaderPc } from '../../components/cells/Header/Global/views/SubHeaderPc.ts';
import homeClass from './Main.module.scss';
import { CategoryProductRankingViewData } from '@services/MarppleShop/renderApp/pages/Main/views/CategoryProductRankingView/CategoryProductRankingView.ts';
import { LimitedEdition, LimitedEditionProps } from './views/LimitedEdition/LimitedEdition';
import { PhotoReview, PhotoReviewProps } from './views/PhotoReview/PhotoReview';
import { PlusBannerProps, PlusBannerView } from './views/PlusBanner/PlusBanner';
import { MainPlusContentSection, PlusContentProps } from './views/PlusContent/PlusContent';
import { PlusCreator, PlusCreatorProps } from './views/PlusCreator/PlusCreator';
import { PlusLive, PlusLiveProps } from './views/PlusLive/PlusLive';
import { SubHeaderData } from '@services/MarppleShop/renderApp/components/cells/Header/libs/types/headerSub';
import { makeUtilScreenNavigate } from '@services/MarppleShop/shared/app/navigate.ts';
import { makePlusUrl } from '@services/MarppleShop/shared/util/url.ts';
import { ProductListSection } from '@services/MarppleShop/renderApp/pages/Main/views/ProductListSection/ProductListSection.ts';

export interface HomePageData {
  plus_crew: Locals['plus_crew'];
  header: SubHeaderData;
  creators: PlusCreatorProps;
  plus_contents: PlusContentProps;
  plus_banner: PlusBannerProps;
  limited_edition: LimitedEditionProps;
  best_product: CategoryProductRankingViewData;
  plus_live: PlusLiveProps;
  photo_review: PhotoReviewProps;
  layout_options: BaseLayoutOptions;
  popup: ClientPopupProps;
}

class MainBodyView extends View<HomePageData> {
  protected override template() {
    const {
      plus_contents,
      creators,
      plus_banner,
      limited_edition,
      best_product,
      plus_live,
      photo_review,
      header,
      popup,
    } = this.data;
    const is_mobile = !!getSharedData(this)?.is_mobile;
    const is_app = !!getSharedData(this)?.webviewapp;

    return html`<div class="${homeClass.container}">
      <div class="${homeClass.bannerContainer}">${new PlusBannerView(plus_banner)}</div>
      <div class="${homeClass.subHeader}">
        ${is_mobile
          ? new SubHeaderMobile(header, {
              reset_on_selected: is_app,
            })
          : new SubHeaderPc(header, {
              transparent_background: false,
            })}
      </div>

      <div data-is-main="false" class="${homeClass.homeContainer}">
        <!-- 크리에이터 -->
        ${creators.popular_creators.length > 0 ? new PlusCreator(creators) : ''}

        <!-- 컨텐츠 -->
        ${plus_contents.contents.length > 0 ? new MainPlusContentSection(plus_contents) : ''}

        <!-- 한정 상품 -->
        ${limited_edition.products.length > 0
          ? new LimitedEdition(
              limited_edition,
              { is_mobile },
              {
                getTitle: () => ET('mps2::plus::limited'),
                getSeeMoreLink: () => {
                  return `/${ET.lang}/@/products?type=limited`;
                },
                getNavigate: () => {
                  return makeUtilScreenNavigate(
                    makePlusUrl(this.data.plus_crew.domain_name, `/${ET.lang}/@/products?type=limited`),
                  );
                },
              },
            )
          : ''}

        <!-- 베스트 상품 -->
        ${best_product.products.length > 0
          ? new ProductListSection({
              layout_options: this.data.layout_options,
              products: best_product.products,
              section_title: best_product.section_title,
            })
          : ''}

        <!-- 라이브 -->
        ${plus_live.lives.length > 0 ? new PlusLive(plus_live) : ''}

        <!-- 포토 리뷰 -->
        ${photo_review.reviews.length > 0 ? new PhotoReview(photo_review) : ''}
      </div>

      ${popup.popups.length > 0 ? new Popup(popup) : ''}
    </div>`;
  }

  override onRender() {
    const is_mobile = !!getSharedData(this)?.is_mobile;
    const sub_header_view = this.subView(is_mobile ? SubHeaderMobile : SubHeaderPc);

    if (sub_header_view) {
      applySubHeaderHeadroom(Header, sub_header_view);
    }
  }
}

export class PlusMainPage extends Page<HomePageData> {
  override onMount() {
    SubHeaderMobile.handleChangeMenuOutside({ enable_post_message: true });
  }

  protected override template() {
    const { layout_options } = this.data;

    const plus_name = sanitizer.unescapeEntities(
      this.data.plus_crew.brand_title || this.data.plus_crew.brand_title_default || 'plus',
    );

    return html`
      <div>
        ${new LayoutView(
          {
            body: new MainBodyView(this.data),
            footer_data: { ...footer_links, lang: ET.lang, is_mobile: layout_options.is_mobile },
          },
          {
            require_top_arrow_button: true,
            full_width: true,
            has_bottom_tab_bar: true,
            selected_bottom_tab_idx: -1,
            floating_box: {
              share: {
                title: plus_name,
                description: ET('mps2::share::plus_main_description', {
                  plus_name,
                }),
              },
            },
            ...layout_options,
          },
        )}
      </div>
    `;
  }
}
